<template>
  <div class="min-h-screen relative overflow-hidden bg-gradient-to-br from-red-50 via-pink-50 to-rose-50 flex items-center justify-center p-4">
    <!-- Particules animées -->
    <div
        v-for="particle in particles"
        :key="particle.id"
        class="absolute w-2 h-2 bg-red-400/30 rounded-full animate-float"
        :style="{
        left: particle.x + '%',
        top: particle.y + '%',
        animationDelay: particle.delay + 's',
        animationDuration: particle.duration + 's'
      }"
    />

    <!-- Contenu principal -->
    <div class="relative z-10 text-center max-w-3xl mx-auto">
      <!-- Icône 404 stylisée -->
      <div class="mb-8 flex justify-center">
        <div class="relative">
          <div class="text-8xl md:text-9xl font-bold text-red-200 select-none">404</div>
          <div class="absolute inset-0 flex items-center justify-center">
            <i class="pi pi-search text-red-500 text-3xl animate-pulse" ></i>
          </div>
        </div>
      </div>

      <!-- Titre -->
      <h1 class="text-3xl md:text-4xl font-bold text-red-600 mb-4 animate-fade-in-up">
        {{ t('errors.notFound.title') }}
      </h1>

      <!-- Message personnalisé -->
      <p class="text-lg text-gray-600 mb-8 animate-fade-in-up animation-delay-300">
        {{ t('errors.notFound.message') }}
        <code class="bg-red-100 text-red-700 px-2 py-1 rounded text-sm">{{ requestedPath }}</code>
      </p>

      <!-- Bouton retour unique -->
      <div class="animate-fade-in-up animation-delay-600">
        <Button
            :label="t('errors.notFound.backHome')"
            icon="pi pi-home"
            class="p-button-lg bg-gradient-to-r from-red-500 to-pink-600 border-0 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
            @click="goHome"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
interface Props {
  requestedPath?: string
}

const props = withDefaults(defineProps<Props>(), {
  requestedPath: '/unknown'
})

const { t } = useI18n()
const router = useRouter()

// Particules
const particles = ref(
    Array.from({ length: 18 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      duration: Math.random() * 3 + 2,
      delay: Math.random() * 2
    }))
)

const goHome = () => {
  navigateTo('/')
}
</script>

<style scoped>
/* Réutilisation des styles de ComingSoon avec adaptations */
@keyframes float {
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-15px) rotate(180deg); }
}

@keyframes fade-in-up {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-float {
  animation: float 3s ease-in-out infinite;
}

.animate-fade-in-up {
  animation: fade-in-up 0.8s ease-out forwards;
}

.animation-delay-300 { animation-delay: 0.3s; }
.animation-delay-600 { animation-delay: 0.6s; }
.animation-delay-900 { animation-delay: 0.9s; }
.animation-delay-1200 { animation-delay: 1.2s; }
.animation-delay-1500 { animation-delay: 1.5s; }

.animate-bounce-slow { animation: bounce 3s infinite; }
.animate-ping-slow {
  animation: ping 3s cubic-bezier(0, 0, 0.2, 1) infinite;
  animation-delay: 1s;
}

:deep(.p-card) { border-radius: 1rem; }
:deep(.p-button) { border-radius: 0.75rem; }
:deep(.p-tag) { border-radius: 0.5rem; }
</style>
